import { casesActions } from "configs";
import { generateId, useCurrentCase, useDispatch, useParams } from "duck";
import { useSetLightMonitoringInformationMutation } from "requests";
import { FormValues } from "./types";

export const useCreateVitalsAndOxygen = () => {
  const dispatch = useDispatch();
  const { id: caseId } = useParams();
  const [setGasAndVital] = useSetLightMonitoringInformationMutation();

  const { shouldSync, patientId, vitals, anestheticGases } = useCurrentCase(
    medicalCase => ({
      shouldSync: medicalCase.shouldSync,
      patientId: medicalCase.data.client.patient._id,
      vitals: medicalCase.data.client?.patient?.vitals,
      anestheticGases: medicalCase.data.client?.patient?.anestheticGases,
      createdAt: medicalCase.data.createdAt
    })
  );

  return (values: FormValues) => {
    const vital = vitals?.find(el => {
      return (
        new Date(el.timeMonitoring).getTime() ===
        values?.timeMonitoring?.getTime()
      );
    });

    const anestheticGas = anestheticGases?.find(el => {
      return (
        new Date(el.timeMonitoring).getTime() ===
        values?.timeMonitoring?.getTime()
      );
    });

    const vitalVariables = {
      timeMonitoring: values.timeMonitoring.toISOString(),
      heartRate: values.heartRate,
      respiratoryRate: values.respiratoryRate,
      pulseOximetry: values.pulseOximetry,
      endTidal: values.endTidal,
      systolicBloodPressure: values.systolicBloodPressure,
      meanBloodPressure: values.meanBloodPressure,
      diastolicBloodPressure: values.diastolicBloodPressure
    };

    const gasVariables = {
      timeMonitoring: values.timeMonitoring.toISOString(),
      inhalantAnestheticAgent: values.inhalantAnestheticAgent,
      oxygen: values.oxygen
    };

    if (shouldSync) {
      return setGasAndVital({
        variables: {
          input: {
            patient: patientId,
            ...gasVariables,
            ...vitalVariables
          }
        }
      }).then(response => {
        if (vital?._id) {
          dispatch(
            casesActions.updateVitals({
              data: {
                _id: vital._id,
                ...vitalVariables
              },
              _id: caseId
            })
          );
        } else {
          const newVital =
            response.data?.setLightMonitoringInformation
              ?.lightMonitoringInformation?.vital;
          if (newVital) {
            dispatch(
              casesActions.addVitals({
                _id: caseId,
                data: newVital
              })
            );
          }
        }

        if (anestheticGas?._id) {
          dispatch(
            casesActions.updateAnestheticGases({
              data: {
                _id: anestheticGas._id,
                ...gasVariables
              },
              _id: caseId
            })
          );
        } else {
          const newAnestheticGas =
            response.data?.setLightMonitoringInformation
              ?.lightMonitoringInformation?.anestheticGas;

          if (newAnestheticGas) {
            dispatch(
              casesActions.addAnestheticGases({
                _id: caseId,
                data: newAnestheticGas
              })
            );
          }
        }
      });
    }

    if (vital?.fakeId) {
      dispatch(
        casesActions.updateVitals({
          data: {
            fakeId: vital.fakeId,
            ...vitalVariables
          },
          _id: caseId
        })
      );
    } else {
      dispatch(
        casesActions.addVitals({
          _id: caseId,
          data: {
            fakeId: generateId(),
            ...vitalVariables
          }
        })
      );
    }

    if (anestheticGas?.fakeId) {
      dispatch(
        casesActions.updateAnestheticGases({
          data: {
            fakeId: anestheticGas.fakeId,
            ...gasVariables
          },
          _id: caseId
        })
      );
    } else {
      dispatch(
        casesActions.addAnestheticGases({
          _id: caseId,
          data: {
            fakeId: generateId(),
            ...gasVariables
          }
        })
      );
    }

    return Promise.resolve();
  };
};
